import React, { ReactNode } from 'react';
import styled from 'styled-components';
import {
  EXPANDED_NODE_TOOLTIP_HEIGHT,
  NODE_HORIZONTAL_SPACE,
  NODE_TOOLTIP_HEIGHT,
} from './constants';
import { BaseContainer } from './TooltipUI';
import { useFullscreenContext } from '../../../utils/contexts/FullscreenContext';
import { useCurrentUrl } from '../../../utils/routing';
import { ChartMode } from '../types';
/**
 * Due to a bug with foreignObjects in Safari:
 * https://bugs.webkit.org/show_bug.cgi?id=23113
 * Component will detect and render a special markup in order to guarantee correct
 * position of Sankey Node tooltips
 */

interface IProps {
  children: ReactNode;
  x: number;
  y: number;
  isObserveMode: boolean;
  mode: ChartMode;
}

function detectSafari() {
  const userAgent = navigator.userAgent;

  // Check for Safari and exclude Chrome and other browsers that might mimic Safari's user agent
  return (
    userAgent.includes('Safari') &&
    !userAgent.includes('Chrome') &&
    !userAgent.includes('Chromium')
  );
}

const StyledForeignObject = styled.foreignObject`
  overflow: visible;
`;

const Wrapper = styled.div`
  position: relative;
  overflow: visible;
`;

const NodeTooltipContainer = styled(BaseContainer)`
  position: absolute;
  left: 0;
  bottom: 0;
  overflow: auto;
`;
const SafariCompactNodeTooltipContainer = styled(BaseContainer)`
  overflow: auto;
`;

const SankeyNodeTooltipContainer = ({
  children,
  x,
  y,
  isObserveMode,
  mode,
}: IProps) => {
  const { isFullScreen } = useFullscreenContext();
  const { isReportInUrl } = useCurrentUrl();
  const notResponsive =
    isFullScreen || (isReportInUrl && mode !== ChartMode.Compact);
  const isSafariBrowser = detectSafari();
  const safariSpecial = isSafariBrowser && !notResponsive;

  const maxHeight = isObserveMode
    ? EXPANDED_NODE_TOOLTIP_HEIGHT
    : NODE_TOOLTIP_HEIGHT;

  return (
    <StyledForeignObject
      x={x}
      y={y}
      width={Math.round(NODE_HORIZONTAL_SPACE * 0.9)}
      height={1}
    >
      {safariSpecial ? (
        <SafariCompactNodeTooltipContainer>
          {children}
        </SafariCompactNodeTooltipContainer>
      ) : (
        <Wrapper>
          <NodeTooltipContainer style={{ maxHeight }}>
            {children}
          </NodeTooltipContainer>
        </Wrapper>
      )}
    </StyledForeignObject>
  );
};

export default SankeyNodeTooltipContainer;
